import type { TransactionScheduleCallbacks } from "@hooks/view/useViewTableMeta";
import React, { useMemo } from "react";
import { Transaction, TransactionStatus } from "@common/types/transaction";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { MatchedScheduleCard } from "@components/accounting/schedules/MatchedScheduleCard";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { useInlineScheduleLockedPeriods } from "@components/accounting/schedules/useInlineScheduleLockedPeriods";
import { usePanelScheduleTemplates } from "@components/accounting/schedules/usePanelScheduleTemplates";
import { useAccrualAccountType } from "@components/accounting/schedules/useAccrualAccountType";
import { filterTemplatesForTransaction } from "@components/accounting/schedules/transactionScheduleTemplates";
import { useTransactionSchedulePicker } from "@components/transactions/views/useTransactionSchedulePicker";
import { TransactionDetailsField } from "./TransactionDetailsField";
import accountFieldStyles from "./TransactionDetailsAccount.module.scss";

interface Props {
    transaction: Transaction;
    isLocked?: boolean;
    scheduleCallbacks?: TransactionScheduleCallbacks;
}

export const TransactionDetailsSchedule: React.FC<Props> = ({
    transaction,
    isLocked,
    scheduleCallbacks,
}) => {
    const {
        schedules: isSchedulesEnabled,
        schedulesExpenses: isExpensesEnabled,
    } = useTypedFlags();
    const entityTemplates = usePanelScheduleTemplates(transaction.entity.id);
    const accountType = useAccrualAccountType(transaction.accrualAccountId);
    const scheduleTemplates = useMemo(
        () =>
            filterTemplatesForTransaction({
                templates: entityTemplates,
                amount: transaction.amount,
                accountType,
                isExpensesEnabled,
            }),
        [accountType, entityTemplates, isExpensesEnabled, transaction.amount],
    );
    const { showLockedPeriods, lockedPeriodsModal } =
        useInlineScheduleLockedPeriods();
    const { matchedSchedules, pickerProps, overlays } =
        useTransactionSchedulePicker({
            transaction,
            isReadonlyOrLocked: Boolean(isLocked),
            scheduleCallbacks,
            showLockedPeriods,
        });

    if (
        !isSchedulesEnabled ||
        transaction.status === TransactionStatus.PENDING
    ) {
        return null;
    }

    if (matchedSchedules.length > 0) {
        return (
            <TransactionDetailsField
                label="Recognition schedule"
                value={
                    <MatchedScheduleCard
                        schedules={matchedSchedules}
                        counterpart="invoice"
                    />
                }
            />
        );
    }

    return (
        <>
            <TransactionDetailsField
                label="Recognition schedule"
                value={
                    <SchedulePicker
                        compact
                        compactTriggerClassName={
                            accountFieldStyles.ledgerTrigger
                        }
                        templates={scheduleTemplates}
                        {...pickerProps}
                    />
                }
            />
            {overlays}
            {lockedPeriodsModal}
        </>
    );
};
